import React from 'react';
import { useSelector } from 'react-redux';
import usePermissions from '../../hooks/auth/usePermissions';
import useOwnerSetupStatus from '../../hooks/onboarding/useOwnerSetupStatus';
import { FeatureFlags } from '../../services/exports/Constants';
import { isInFirstLaunchOnboarding } from '../../services/tenant/plgOnboarding';
import OwnerSetupCard from './OwnerSetupCard';

/**
 * Restaurant-owner entry-point for the partner two-actor onboarding flow
 * (FOO-6351 C10, D4 default). When a restaurant owner (merchant / StoreAdmin)
 * whose account was provisioned by a partner logs in with their setup still
 * incomplete, this renders the persistent "Setting up" card that deep-links
 * them into the wizard at their first incomplete phase.
 *
 * Renders nothing for partners / master providers / non-owners, and nothing
 * once the restaurant is live — so it is inert for everyone but the owner of a
 * restaurant still being set up. Once the owner's own steps are done it says
 * so: going live is the partner's (ContactProviderGate), and "Continue setup"
 * would only lead to steps they cannot do. Nothing either when the steps could
 * not be read: a card whose CTA ends on a load error is worse than none.
 * Mounted once in the dashboard Layout so it persists across navigation (the
 * store-user analog of the partner-facing `OnboardingGuide`).
 *
 * Additionally gated on the PLG onboarding feature flag: only providers that
 * have `plg_onboarding` enabled get the two-actor flow at all. Without the
 * flag the owner will never have been provisioned via the wizard and the
 * generic dashboard experience applies.
 */
export default function OwnerOnboardingEntry() {
  const { isMerchant } = usePermissions();
  const company = useSelector((state) => state.currentCompany.company);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  // Gate on the provider-level PLG flag, not on `useIsPartnerStaff` — this
  // component renders for merchants (StoreAdmins), who are not partner staff.
  // A merchant whose provider has the flag was provisioned through the wizard
  // two-actor flow; one whose provider lacks it was not, and the onboarding
  // steps the hook depends on will never exist for them.
  const providerHasPlg = (serviceProvider?.enabled_feature_flags ?? []).includes(FeatureFlags.PlgOnboarding);
  const enabled = isMerchant && providerHasPlg && Boolean(company?.id);
  const { initialised, failed, isComplete, phases, doneCount } = useOwnerSetupStatus(enabled);

  if (!enabled || !initialised || failed) {
    return null;
  }

  if (isComplete) {
    return isInFirstLaunchOnboarding(serviceProvider, company) ? (
      <OwnerSetupCard companyId={company.id} partnerName={serviceProvider?.name} done />
    ) : null;
  }

  return <OwnerSetupCard companyId={company.id} phases={phases} doneCount={doneCount} />;
}
